import {
  applyTurn,
  createInitialState,
  isInEnemyTerritory,
  squareKey,
  type GameState,
} from "../engine";
import { chooseBestAction, reviewMove } from "../bot/minimax";
import { computePhase, narrateMove, narrateEngineReview } from "./narrate";
import type { GamePhase, TutorialStep } from "./script";

export interface GeneratedTutorial {
  initialState: GameState;
  steps: TutorialStep[];
}

/**
 * Build a complete narrated tutorial game from the canonical opening position.
 *
 * Both sides are now driven by depth-1 minimax (not greedy) so moves carry
 * coherent positional intent — no more retreats with no apparent cause. After
 * each move is chosen, we run a deeper review (depth 3) against the original
 * position to surface "engine assessment" prose: was this the best move, and
 * if not, what was the engine's preferred line and why? See bot/minimax.ts.
 *
 * The narrator surfaces rule explanations on first-of-kind events (first
 * Triangle leap, first Square move, first Pyramid mode, first capture,
 * first territory crossing, first Assault, first Siege).
 *
 * Stops at the configured max number of turns or when victory triggers,
 * whichever is first.
 */
const PLAY_DEPTH = 1;       // shallow but coherent — keeps generation fast
const PLAY_BUDGET_MS = 100; // per-move hard ceiling for the play search
const REVIEW_DEPTH = 2;     // shallow review still catches most missed tactics
const REVIEW_BUDGET_MS = 120;

export function generateTutorialScript(maxTurns = 30): GeneratedTutorial {
  const initialState = createInitialState();
  const steps: TutorialStep[] = [];

  let state = initialState;
  let seenTriangleLeap = false;
  let seenSquareMove = false;
  let seenPyramidMove = false;
  let seenCapture = false;
  let seenCrossing = false;
  let seenAssault = false;
  let seenSiege = false;

  for (let i = 0; i < maxTurns && !state.victory; i++) {
    const side = state.toMove;
    const action = chooseBestAction(state, side, {
      maxDepth: PLAY_DEPTH,
      iterativeDeepening: false,
      deadlineMs: performance.now() + PLAY_BUDGET_MS,
    });
    if (!action) break;

    const pre = state;
    const piece = pre.board.get(squareKey(action.move.from));
    if (!piece) break;

    const result = applyTurn(state, action.move, {
      declared: action.declared,
      advance: action.advance,
    });
    const post = result.state;

    const isTriangleMove = piece.shape === "triangle" || action.move.mode === "triangle";
    const isSquareMove = piece.shape === "square" || action.move.mode === "square";
    const isPyramidMove = piece.shape === "pyramid";
    const hadAnyCapture = result.pending.sieges.length > 0 || !!action.declared;
    const crossed = isInEnemyTerritory(piece.side, action.move.to.rank);
    const wasAssault = action.declared?.method === "assault";
    const wasSiege = result.pending.sieges.length > 0;

    const firstTriangleLeap = isTriangleMove && !seenTriangleLeap;
    const firstSquareMove = isSquareMove && !seenSquareMove;
    const firstPyramidMove = isPyramidMove && !seenPyramidMove;
    const firstCaptureOverall = hadAnyCapture && !seenCapture;
    const firstCrossing = crossed && !seenCrossing;
    const firstAssault = wasAssault && !seenAssault;
    const firstSiege = wasSiege && !seenSiege;

    if (firstTriangleLeap) seenTriangleLeap = true;
    if (firstSquareMove) seenSquareMove = true;
    if (firstPyramidMove) seenPyramidMove = true;
    if (firstCaptureOverall) seenCapture = true;
    if (firstCrossing) seenCrossing = true;
    if (firstAssault) seenAssault = true;
    if (firstSiege) seenSiege = true;

    const moveNumber = i + 1;
    const phase: GamePhase = computePhase(moveNumber);
    const prevPhase: GamePhase | null = moveNumber > 1 ? computePhase(moveNumber - 1) : null;
    // A "phase transition" marker fires on turn 1 (entering the opening) and at every
    // boundary where the computed phase changes. Drives the phase preamble + badge.
    const phaseTransition = moveNumber === 1 || phase !== prevPhase;

    const { title, narration, highlights, why, watchFor } = narrateMove({
      pre,
      post,
      move: action.move,
      declared: action.declared,
      advance: action.advance,
      sieges: result.pending.sieges,
      moveNumber,
      side,
      phase,
      phaseTransition,
      firstTriangleLeap,
      firstSquareMove,
      firstPyramidMove,
      firstCaptureOverall,
      firstCrossing,
      firstAssault,
      firstSiege,
    });

    // Engine review: was this the move the engine would have played? If not, what would it
    // have preferred, and what's the one-line reason? Done at a deeper depth than the play
    // search so the review can actually catch a missed tactic.
    const review = reviewMove(state, side, action, {
      maxDepth: REVIEW_DEPTH,
      iterativeDeepening: false,
      deadlineMs: performance.now() + REVIEW_BUDGET_MS,
    });
    const reviewAnnotation = review ? narrateEngineReview(state, side, action, review) : undefined;

    steps.push({
      move: action.move,
      declared: action.declared,
      advance: action.advance ?? false,
      title,
      narration,
      highlights,
      phase,
      why,
      watchFor,
      review: reviewAnnotation,
    });

    state = post;
  }

  return { initialState, steps };
}
